import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, Filter, X, ChevronDown, Check } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { motion, AnimatePresence } from "framer-motion";

interface TopicFiltersProps {
  searchQuery: string;
  setSearchQuery: (value: string) => void;
  typeFilter: string;
  setTypeFilter: (value: string) => void;
  audienceFilter: string;
  setAudienceFilter: (value: string) => void;
  availableAudiences?: string[];
}

export const TopicFilters = ({
  searchQuery,
  setSearchQuery,
  typeFilter,
  setTypeFilter,
  audienceFilter,
  setAudienceFilter,
  availableAudiences = [],
}: TopicFiltersProps) => {
  const [showFilters, setShowFilters] = useState(false);

  const handleClearFilters = () => {
    setTypeFilter("all");
    setAudienceFilter("all");
    setSearchQuery("");
  };

  const hasActiveFilters = typeFilter !== "all" || audienceFilter !== "all" || searchQuery !== "";
  const activeFilterCount = (typeFilter !== "all" ? 1 : 0) + (audienceFilter !== "all" ? 1 : 0);

  // Content type options
  const contentTypes = [
    { value: "all", label: "All Types" },
    { value: "how-to", label: "How-To Guide" },
    { value: "listicle", label: "Listicle" },
    { value: "tutorial", label: "Tutorial" },
    { value: "faq", label: "FAQ" },
    { value: "educational", label: "Educational" },
    { value: "checklist", label: "Checklist" },
    { value: "comparison", label: "Comparison" },
    { value: "trend", label: "Trend Analysis" },
    { value: "data", label: "Data Report" },
  ];

  // Create audience options from only available audiences
  const audiences = [
    { value: "all", label: "All Audiences" },
    ...availableAudiences.map(audience => ({
      value: audience,
      label: audience.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
    }))
  ];

  // Get the current type and audience labels
  const currentTypeLabel = contentTypes.find(type => type.value === typeFilter)?.label || "All Types";
  const currentAudienceLabel = audiences.find(audience => audience.value === audienceFilter)?.label || "All Audiences";

  return (
    <div className="space-y-3">
      <div className="flex flex-col sm:flex-row gap-3">
        {/* Search Input */}
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400 dark:text-zinc-500" />
          <Input
            type="text"
            placeholder="Search topics..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 bg-white dark:bg-zinc-800 border-zinc-200 dark:border-zinc-700 focus-visible:ring-blue-500 dark:text-white"
          />
          {searchQuery && (
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-2 top-1/2 h-6 w-6 -translate-y-1/2 rounded-full text-zinc-400 hover:text-zinc-600"
              onClick={() => setSearchQuery("")}
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
        
        {/* Filter Dropdowns */}
        <div className="flex gap-3">
          {/* Type Filter */}
          <div className="w-40">
            <Select
              value={typeFilter}
              onValueChange={setTypeFilter}
            >
              <SelectTrigger className="bg-white dark:bg-zinc-800 border-zinc-200 dark:border-zinc-700 dark:text-white">
                <div className="flex items-center gap-2">
                  <Filter className="h-3.5 w-3.5 text-zinc-400 dark:text-zinc-500" />
                  <SelectValue placeholder="Type" />
                </div>
              </SelectTrigger>
              <SelectContent className="dark:bg-zinc-800 dark:border-zinc-700">
                <SelectItem value="all" className="dark:text-white">All Types</SelectItem>
                <SelectItem value="how-to" className="dark:text-white">How-To Guide</SelectItem>
                <SelectItem value="listicle" className="dark:text-white">Listicle</SelectItem>
                <SelectItem value="tutorial" className="dark:text-white">Tutorial</SelectItem>
                <SelectItem value="faq" className="dark:text-white">FAQ</SelectItem>
                <SelectItem value="educational" className="dark:text-white">Educational</SelectItem>
                <SelectItem value="checklist" className="dark:text-white">Checklist</SelectItem>
                <SelectItem value="comparison" className="dark:text-white">Comparison</SelectItem>
                <SelectItem value="trend" className="dark:text-white">Trend Analysis</SelectItem>
                <SelectItem value="data" className="dark:text-white">Data Report</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Audience Filter */}
          <div className="w-44">
            <Select
              value={audienceFilter}
              onValueChange={setAudienceFilter}
            >
              <SelectTrigger className="bg-white dark:bg-zinc-800 border-zinc-200 dark:border-zinc-700 dark:text-white">
                <div className="flex items-center gap-2">
                  <Filter className="h-3.5 w-3.5 text-zinc-400 dark:text-zinc-500" />
                  <SelectValue placeholder="Audience" />
                </div>
              </SelectTrigger>
              <SelectContent className="dark:bg-zinc-800 dark:border-zinc-700">
                {audiences.map(audience => (
                  <SelectItem key={audience.value} value={audience.value} className="dark:text-white">
                    {audience.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Active Filters */}
      <AnimatePresence>
        {hasActiveFilters && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="flex flex-wrap items-center gap-2"
          >
            {typeFilter !== "all" && (
              <Badge 
                variant="outline" 
                className="bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-blue-100 dark:border-blue-800/30 hover:bg-blue-100 dark:hover:bg-blue-800/50 px-2.5 py-1 flex items-center gap-1.5"
              >
                Type: {currentTypeLabel}
                <X 
                  className="h-3 w-3 cursor-pointer" 
                  onClick={(e) => {
                    e.stopPropagation();
                    setTypeFilter("all");
                  }} 
                />
              </Badge>
            )}
            
            {audienceFilter !== "all" && (
              <Badge 
                variant="outline" 
                className="bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-blue-100 dark:border-blue-800/30 hover:bg-blue-100 dark:hover:bg-blue-800/50 px-2.5 py-1 flex items-center gap-1.5"
              >
                Audience: {currentAudienceLabel}
                <X 
                  className="h-3 w-3 cursor-pointer" 
                  onClick={(e) => {
                    e.stopPropagation();
                    setAudienceFilter("all");
                  }} 
                />
              </Badge>
            )}
            
            {searchQuery && (
              <Badge 
                variant="outline" 
                className="bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 border-emerald-100 dark:border-emerald-800/30 hover:bg-emerald-100 dark:hover:bg-emerald-800/50 px-2.5 py-1 flex items-center gap-1.5"
              >
                Search: "{searchQuery}"
                <X 
                  className="h-3 w-3 cursor-pointer" 
                  onClick={(e) => {
                    e.stopPropagation();
                    setSearchQuery("");
                  }} 
                />
              </Badge>
            )}
            
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClearFilters}
              className="h-7 text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800 px-2 rounded-md"
            >
              Clear all
            </Button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
